"use client";

import Link from "next/link";

/**
 * FullScreenError — shared full-screen error state used by the /o/[org] layout
 * guard and the /o resolver when org memberships fail to load or are absent.
 *
 * Props:
 *   onRetry   — called when the user clicks Try again (e.g. refreshOrgs)
 *   onSignOut — if provided, renders a "Sign out" button instead of the
 *               "Back to sign in" link, which avoids a redirect loop for
 *               authenticated-but-broken sessions (the link sends the browser
 *               to /login which immediately bounces them back here).
 *   message   — override the default copy
 */
interface FullScreenErrorProps {
  onRetry?: () => void;
  onSignOut?: () => void;
  message?: string;
}

export default function FullScreenError({
  onRetry,
  onSignOut,
  message = "We couldn't load your workspaces.",
}: FullScreenErrorProps) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-paper">
      <div className="flex flex-col items-center gap-6 px-6 text-center">
        {/* Icon — a document with a broken signal, in brand amber */}
        <div className="relative">
          {/* Outer ring — faint amber glow */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(212,160,18,0.12) 0%, transparent 70%)",
              transform: "scale(2.2)",
            }}
            aria-hidden
          />
          <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-brand-200 bg-brand-50">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              className="h-8 w-8 text-brand-600"
              aria-hidden
            >
              {/* Exclamation mark in a rounded diamond */}
              <path
                d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="12"
                y1="9"
                x2="12"
                y2="13"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
              <line
                x1="12"
                y1="17"
                x2="12.01"
                y2="17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        {/* Heading */}
        <div className="flex flex-col gap-1.5">
          <h1 className="font-display text-xl font-semibold tracking-tight text-ink">
            Something went wrong
          </h1>
          <p className="max-w-xs text-sm leading-relaxed text-ink-lighter">
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col items-center gap-3">
          {onRetry && (
            <button
              onClick={onRetry}
              className="group inline-flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-medium text-white transition-all duration-150 hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper active:scale-95"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-rotate-180"
                aria-hidden
              >
                <path
                  d="M14 8A6 6 0 1 1 8 2a6 6 0 0 1 4.243 1.757L14 2v4h-4l1.586-1.586A4 4 0 1 0 12 8h2z"
                  fill="currentColor"
                />
              </svg>
              Try again
            </button>
          )}
          {onSignOut ? (
            <button
              onClick={onSignOut}
              className="text-xs text-ink-lighter transition-colors duration-150 hover:text-ink"
            >
              ← Sign out
            </button>
          ) : (
            <Link
              href="/login"
              className="text-xs text-ink-lighter transition-colors duration-150 hover:text-ink"
            >
              ← Back to sign in
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
